import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Add, Clear, Download, Refresh, ViewColumnOutlined } from "@mui/icons-material";
import { Badge, Button, Chip, FormLabel, IconButton, Popover, Stack, Tooltip } from "@mui/material";
import { ColumnsPanelTrigger, GridFilterListIcon, Toolbar as GridToolbar, QuickFilter, QuickFilterControl, ToolbarButton } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Autocomplete } from "./Autocomplete";
import { DateInput } from "./DateInput";
import { toFilterChips } from "./filterFields";
import { Form } from "./Form";
import { FormButton } from "./FormButton";
import { Input } from "./Input";
import { SwitchInput } from "./SwitchInput";
const panelPadding = 2;
/** minHeight 0 is required: a flex item defaults to min-height auto, which
 * prevents shrinking and stops the region from ever scrolling. */ const fieldsSx = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowY: 'auto',
    pb: 0,
    pt: panelPadding,
    px: panelPadding
};
const footerSx = {
    p: panelPadding
};
const renderFilterInput = (def)=>{
    switch(def.type){
        case 'number':
        case 'text':
            return /*#__PURE__*/ _jsx(Input, {
                "data-testid": def.name,
                format: def.type === 'number' ? def.format ?? 'number' : 'string',
                fullWidth: true,
                hideError: true,
                label: def.label,
                min: def.type === 'number' ? def.min : undefined,
                name: def.name,
                size: "small"
            }, def.name);
        case 'autocomplete':
        case 'multiSelect':
        case 'select':
            {
                const shared = {
                    'data-testid': def.name,
                    fullWidth: true,
                    getOptionLabel: (option)=>option.label,
                    getOptionValue: (option)=>option.value,
                    hideError: true,
                    label: def.label,
                    loading: def.isLoading,
                    name: def.name,
                    noOptionsText: 'No options',
                    options: def.options
                };
                const remote = def.type !== 'select' && def.onSearch ? {
                    onInputChange: def.onSearch
                } : {};
                return def.type === 'multiSelect' ? /*#__PURE__*/ _jsx(Autocomplete, {
                    multiple: true,
                    ...shared,
                    ...remote
                }, def.name) : /*#__PURE__*/ _jsx(Autocomplete, {
                    ...shared,
                    ...remote
                }, def.name);
            }
        case 'switch':
            return /*#__PURE__*/ _jsx(SwitchInput, {
                "data-testid": def.name,
                label: def.label,
                name: def.name
            }, def.name);
        case 'dateRange':
        case 'numberRange':
            {
                const groupId = `filter-group-${def.startName}`;
                const renderBound = (name, suffix)=>def.type === 'dateRange' ? /*#__PURE__*/ _jsx(DateInput, {
                        "data-testid": name,
                        fullWidth: true,
                        hideError: true,
                        hideLabel: true,
                        label: `${def.label} ${suffix}`,
                        name: name,
                        size: "small"
                    }, name) : /*#__PURE__*/ _jsx(Input, {
                        "data-testid": name,
                        format: def.format ?? 'number',
                        fullWidth: true,
                        hideError: true,
                        hideLabel: true,
                        label: `${def.label} ${suffix}`,
                        name: name,
                        size: "small"
                    }, name);
                return /*#__PURE__*/ _jsxs(Stack, {
                    "aria-labelledby": groupId,
                    role: "group",
                    spacing: 0.5,
                    sx: {
                        minWidth: 0
                    },
                    children: [
                        /*#__PURE__*/ _jsx(FormLabel, {
                            id: groupId,
                            children: def.label
                        }),
                        /*#__PURE__*/ _jsxs(Stack, {
                            direction: "row",
                            spacing: 1,
                            sx: {
                                minWidth: 0
                            },
                            children: [
                                renderBound(def.startName, 'From'),
                                renderBound(def.endName, 'To')
                            ]
                        })
                    ]
                }, def.startName);
            }
    }
};
export const FilterToolbar = (props)=>{
    const [anchorEl, setAnchorEl] = useState(null);
    const isOpen = !!anchorEl;
    /* filterDefs inputs are react-hook-form-bound; the toolbar owns their form so
     the consuming page never has to wrap the grid in a <Form>. The popover holds
     the draft; onSearch commits it (chips/query read the applied values). */ const methods = useForm({
        values: props.filterValues
    });
    /* consumers pass onDraftChange inline; a ref keeps the watch subscription
     alive across renders so the values-sync reset notification isn't dropped */ const onDraftChangeRef = useRef(props.onDraftChange);
    onDraftChangeRef.current = props.onDraftChange;
    useEffect(()=>{
        const subscription = methods.watch((values)=>onDraftChangeRef.current?.({
                ...values
            }));
        return ()=>subscription.unsubscribe();
    }, [
        methods
    ]);
    const onOpen = (event)=>setAnchorEl(event.currentTarget);
    const onClose = ()=>setAnchorEl(null);
    const onApply = ()=>{
        props.onSearch?.();
        onClose();
    };
    const onSubmit = methods.handleSubmit((values)=>{
        props.onApplyFilters?.(values);
        onClose();
    });
    const onKeyDown = (event)=>{
        if (event.key === 'Enter' && !event.defaultPrevented) {
            event.preventDefault();
            onApply();
        }
    };
    /**
   * A remote def's options only hold the current search, so pin selected
   * values back in or their input blanks and their chip shows the raw value.
   */ const draftValues = methods.watch();
    const seenLabelsRef = useRef(new Map());
    const filterDefs = props.filterDefs?.map((def)=>{
        if (def.type !== 'autocomplete' && def.type !== 'multiSelect' || !def.onSearch) {
            return def;
        }
        const seen = seenLabelsRef.current.get(def.name) ?? new Map();
        seenLabelsRef.current.set(def.name, seen);
        def.options.forEach((option)=>seen.set(option.value, option.label));
        const selected = [
            ...new Set([
                props.filterValues?.[def.name],
                draftValues[def.name]
            ].flat().filter((value)=>!!value))
        ];
        const missing = selected.filter((value)=>!def.options.some((option)=>option.value === value));
        if (!missing.length) return def;
        return {
            ...def,
            options: [
                ...def.options,
                ...missing.map((value)=>({
                        label: seen.get(value) ?? value,
                        value
                    }))
            ]
        };
    });
    const chips = filterDefs && props.filterValues && props.onFilterChange ? toFilterChips(filterDefs, props.filterValues, props.onFilterChange, props.collapseChipsAfter) : props.chips ?? [];
    const hasChips = chips.length > 0;
    const hasFields = !!props.fields || !!props.filterDefs?.length;
    return /*#__PURE__*/ _jsxs(GridToolbar, {
        children: [
            hasFields && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Tooltip, {
                        title: "Filters",
                        children: /*#__PURE__*/ _jsx(ToolbarButton, {
                            "data-component": "button",
                            "data-testid": props.triggerTestId ?? 'filterTrigger',
                            onClick: onOpen,
                            children: /*#__PURE__*/ _jsx(Badge, {
                                badgeContent: chips.length,
                                color: "primary",
                                invisible: !hasChips,
                                overlap: "circular",
                                children: /*#__PURE__*/ _jsx(GridFilterListIcon, {})
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Popover, {
                        anchorEl: anchorEl,
                        anchorOrigin: {
                            horizontal: 'left',
                            vertical: 'bottom'
                        },
                        onClose: onClose,
                        open: isOpen,
                        slotProps: {
                            paper: {
                                sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: 'calc(100vh - 96px)',
                                    maxWidth: 'calc(100vw - 32px)',
                                    p: 0,
                                    width: '23.75rem'
                                }
                            }
                        },
                        transformOrigin: {
                            horizontal: 'left',
                            vertical: 'top'
                        },
                        children: filterDefs ? /*#__PURE__*/ _jsxs(Form, {
                            methods: methods,
                            onSubmit: onSubmit,
                            children: [
                                /*#__PURE__*/ _jsx(Stack, {
                                    spacing: 2,
                                    sx: fieldsSx,
                                    children: filterDefs.map(renderFilterInput)
                                }),
                                /*#__PURE__*/ _jsx(Stack, {
                                    sx: footerSx,
                                    children: /*#__PURE__*/ _jsx(FormButton, {
                                        "data-testid": props.searchButtonTestId ?? 'filterApply',
                                        fullWidth: true,
                                        triggerValidation: true,
                                        variant: "contained",
                                        children: "Search"
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(Stack, {
                            onKeyDown: onKeyDown,
                            sx: {
                                display: 'contents'
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Stack, {
                                    spacing: 2,
                                    sx: fieldsSx,
                                    children: props.fields
                                }),
                                /*#__PURE__*/ _jsx(Stack, {
                                    sx: footerSx,
                                    children: /*#__PURE__*/ _jsx(Button, {
                                        "data-testid": props.searchButtonTestId ?? 'filterApply',
                                        fullWidth: true,
                                        onClick: onApply,
                                        variant: "contained",
                                        children: "Search"
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                alignItems: "center",
                direction: "row",
                flexWrap: "wrap",
                sx: {
                    flex: 1,
                    gap: 0.5,
                    minWidth: 0,
                    ml: 1
                },
                children: [
                    chips.map((chip)=>/*#__PURE__*/ _jsx(Chip, {
                            "data-testid": `filterChip-${chip.key}`,
                            deleteIcon: props.chipDeleteIcon,
                            label: chip.label,
                            onDelete: chip.onRemove
                        }, chip.key)),
                    hasChips && props.onClearAll && /*#__PURE__*/ _jsx(IconButton, {
                        color: "inherit",
                        "data-testid": "filterClearAll",
                        onClick: props.onClearAll,
                        children: /*#__PURE__*/ _jsx(Clear, {})
                    })
                ]
            }),
            props.showQuickFilter && /*#__PURE__*/ _jsx(QuickFilter, {
                defaultExpanded: true,
                expanded: true,
                children: /*#__PURE__*/ _jsx(QuickFilterControl, {
                    "aria-label": "Search",
                    "data-component": "input",
                    "data-testid": props.quickFilterTestId ?? 'filterQuickSearch',
                    placeholder: "Search…",
                    size: "small"
                })
            }),
            props.showColumnsButton && /*#__PURE__*/ _jsx(Tooltip, {
                title: "Columns",
                children: /*#__PURE__*/ _jsx(ColumnsPanelTrigger, {
                    render: /*#__PURE__*/ _jsx(IconButton, {
                        color: "inherit",
                        "data-testid": "filterColumns",
                        children: /*#__PURE__*/ _jsx(ViewColumnOutlined, {})
                    })
                })
            }),
            (props.onRefresh ?? props.onSearch) && /*#__PURE__*/ _jsx(Tooltip, {
                title: "Refresh",
                children: /*#__PURE__*/ _jsx(IconButton, {
                    color: "inherit",
                    "data-testid": props.refreshTestId ?? 'filterRefresh',
                    onClick: props.onRefresh ?? props.onSearch,
                    children: /*#__PURE__*/ _jsx(Refresh, {})
                })
            }),
            props.onDownload && /*#__PURE__*/ _jsx(Tooltip, {
                title: "Export",
                children: /*#__PURE__*/ _jsx(IconButton, {
                    color: "inherit",
                    "data-testid": props.downloadTestId ?? 'filterDownload',
                    onClick: props.onDownload,
                    children: /*#__PURE__*/ _jsx(Download, {})
                })
            }),
            props.onAdd && /*#__PURE__*/ _jsx(Tooltip, {
                title: props.addTooltip ?? `Add ${props.addLabel ?? ''}`.trim(),
                children: /*#__PURE__*/ _jsx("span", {
                    children: /*#__PURE__*/ _jsx(IconButton, {
                        color: "inherit",
                        "data-testid": props.addTestId ?? 'filterAdd',
                        disabled: props.addDisabled,
                        onClick: props.onAdd,
                        children: props.addIcon ?? /*#__PURE__*/ _jsx(Add, {})
                    })
                })
            }),
            props.actions
        ]
    });
};
FilterToolbar.displayName = 'FilterToolbar';

//# sourceMappingURL=FilterToolbar.js.map